import React from 'react';
import FieldLabelRow from './FieldLabelRow';
import { StatusBadge } from './StatusBadge';
import DatePickerField from './DatePickerField';
import { SensitiveRevealButton, maskValue } from './SensitiveFieldDisplay';
import { formatDate, parseLocalDate, toLocalISODate } from '../../utils/dateUtils';

/**
 * Shared pass / passport / bond date field with expiry badges and masking.
 *
 * Admin (read-only grids) and employee Profile (optional edit + DatePicker)
 * both use this so expiry styling and reveal behaviour stay in sync.
 *
 * `kind="expiry"` (default) is for end-of-validity dates only. Issue / start
 * dates must use `kind="issued"` — a past issue date is the happy path, not
 * an expired pass. HR reads a red "Expired" chip as "this worker cannot work".
 */

const toDateOnlyIso = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toLocalISODate(value);
  }
  const str = String(value);
  if (str.length >= 10 && str[4] === '-' && str[7] === '-') {
    return str.slice(0, 10);
  }
  return str;
};

/** Calendar-day delta from today (local) to a YYYY-MM-DD value. */
export const passDateDaysUntil = (dateValue) => {
  const parsed = parseLocalDate(toDateOnlyIso(dateValue));
  if (!parsed) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((parsed - today) / (1000 * 60 * 60 * 24));
};

export const isPassExpired = (expiryDate) => {
  const days = passDateDaysUntil(expiryDate);
  return days !== null && days < 0;
};

export const isPassExpiringSoon = (expiryDate, reminderDays = 90) => {
  const days = passDateDaysUntil(expiryDate);
  return days !== null && days >= 0 && days <= reminderDays;
};

const BADGE_BY_THEME = {
  admin: {
    expired: { status: 'expired' },
    expiring: { status: 'expiring_soon' },
  },
  employee: {
    expired: { status: 'rejected', label: 'Expired' },
    expiring: { status: 'pending', label: 'Expiring Soon' },
  },
};

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string|null|undefined} props.value
 * @param {string} props.fieldName - key for visibility toggle
 * @param {'expiry'|'issued'} [props.kind='expiry'] - expiry dates get badges; issued dates never do
 * @param {'admin'|'employee'} [props.theme='admin']
 * @param {'field'|'form'} [props.labelTheme] - defaults: admin→field, employee→form
 * @param {'default'|'comfortable'} [props.size='comfortable']
 * @param {number} [props.reminderDays=90]
 * @param {boolean} [props.isVisible=false]
 * @param {function} [props.onToggleVisibility]
 * @param {React.ReactNode} [props.action] - trailing micro control (admin bond edit)
 * @param {string} [props.className]
 * @param {boolean} [props.isEditing=false]
 * @param {string} [props.name] - form field name when editing
 * @param {function} [props.onChange] - receives synthetic { target: { name, value } }
 * @param {string} [props.error]
 * @param {React.ReactNode} [props.icon]
 * @param {string} [props.emptyLabel='Not provided']
 */
export default function PassDateFieldDisplay({
  label,
  value,
  fieldName,
  kind = 'expiry',
  theme = 'admin',
  labelTheme,
  size = 'comfortable',
  reminderDays = 90,
  isVisible = false,
  onToggleVisibility,
  action = null,
  className = '',
  isEditing = false,
  name,
  onChange,
  error,
  icon,
  emptyLabel = 'Not provided',
}) {
  const resolvedLabelTheme = labelTheme || (theme === 'employee' ? 'form' : 'field');
  const badges = BADGE_BY_THEME[theme] || BADGE_BY_THEME.admin;
  const trackExpiry = kind !== 'issued';
  const isExpired = trackExpiry && isPassExpired(value);
  const isExpiring = trackExpiry && !isExpired && isPassExpiringSoon(value, reminderDays);

  const displayText = (() => {
    if (!value) return emptyLabel;
    if (isVisible) return formatDate(value);
    return maskValue(value, 'date');
  })();

  return (
    <div className={className}>
      <FieldLabelRow
        theme={resolvedLabelTheme}
        label={label}
        size={size}
        icon={icon}
        action={action}
      >
        {value && isExpired && (
          <StatusBadge
            status={badges.expired.status}
            label={badges.expired.label}
            showIcon
          />
        )}
        {value && !isExpired && isExpiring && (
          <StatusBadge
            status={badges.expiring.status}
            label={badges.expiring.label}
            showIcon
          />
        )}
      </FieldLabelRow>
      <div className="relative">
        {isEditing ? (
          <DatePickerField
            theme={theme === 'admin' ? 'admin' : 'employee'}
            selected={parseLocalDate(value)}
            onChange={(date) => {
              if (typeof onChange !== 'function') return;
              onChange({
                target: {
                  name,
                  value: date ? toLocalISODate(date) : '',
                },
              });
            }}
            placeholderText="Select date"
          />
        ) : (
          <p
            className={`field-display ${
              isExpired
                ? 'text-red-900 !bg-red-50 !border-red-200 field-display-sensitive'
                : isExpiring
                  ? 'text-orange-900 !bg-orange-50 !border-orange-200 field-display-sensitive'
                  : `${value ? 'field-display-sensitive' : ''}`
            }`}
          >
            {displayText}
          </p>
        )}
        {!isEditing && value && typeof onToggleVisibility === 'function' && (
          <SensitiveRevealButton
            isVisible={!!isVisible}
            onClick={() => onToggleVisibility(fieldName)}
            theme={theme}
            titleShow="Show pass date"
            titleHide="Hide pass date"
          />
        )}
        {error && <p className="form-error">{error}</p>}
      </div>
    </div>
  );
}
